import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Armchair, ChefHat, Landmark, Power, Inbox,
  Volume2, VolumeX, Check, BellRing, Banknote, Hand, Clock, Menu, X,
  MessageCircle, Wine,
} from "lucide-react";
import Logo from "./Logo";
import { groupItems } from "./ClienteView";
import { SPRING, useMotion } from "../motion";
import { money, elapsedMin, fmtTime, fmtDateTime, post, patch, TABLE_STATUS, METHOD_LABELS, KITCHEN_CATS, orderNo } from "../api";

const COLUMNS = [
  { id: "nuevo", label: "nuevos" },
  { id: "en_cocina", label: "en cocina" },
  { id: "listo", label: "listos para servir" },
  { id: "entregado", label: "entregados · esperando pago" },
];
const NEXT = { nuevo: "en_cocina", en_cocina: "listo", listo: "entregado" };

function cardTone(order) {
  const mins = elapsedMin(order.created_at);
  if (order.status === "entregado") return { bg: "bg-white", border: "border-[#E5E7EB]" };
  if (mins >= 25) return { bg: "bg-[#FEE2E2]", border: "border-[#DC2626]" };
  if (mins >= 15) return { bg: "bg-[#FEF9C3]", border: "border-[#EAB308]" };
  return { bg: "bg-white", border: "border-[#E5E7EB]" };
}

export default function GerenciaView({ state, setState, refresh, muted, setMuted, onEvent, isMobile, section, setSection }) {
  const [selectedTable, setSelectedTable] = useState(null);
  const mo = useMotion();
  const [dragOver, setDragOver] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const alerts = (state?.alerts || []).filter((a) => a.status === "pendiente");
  const orders = state?.orders || [];
  const tables = state?.tables || [];
  const dishes = state?.dishes || [];
  const cash = state?.cash || {};
  const reviews = (state?.reviews || []).filter((r) => r.route === "privado");

  const moveOrder = async (orderId, status) => {
    const res = await patch(`/orders/${orderId}/status`, { status });
    setState(res.state);
  };

  const resolveAlert = async (id) => {
    const res = await post(`/alerts/${id}/resolve`, {});
    setState(res.state);
  };

  const confirmCash = async (table, tip = 0) => {
    const res = await post("/payments/confirm-cash", { table, method: "efectivo", tip });
    setState(res.state);
    onEvent("cobro_confirmado", res.receipt);
  };

  const closeCash = async () => { const res = await post("/cash/close", {}); setState(res.state); };
  const openCash = async () => { const res = await post("/cash/open", {}); setState(res.state); };
  const toggleDish = async (d) => { const res = await patch(`/dishes/${d.id}`, { available: !d.available }); setState(res.state); };
  const handleReview = async (id) => { const res = await post(`/reviews/${id}/handle`, {}); setState(res.state); };
  const freeTable = async (n) => { const res = await patch(`/tables/${n}/status`, { status: "libre" }); setState(res.state); };

  const tableOrders = (n) => orders.filter((o) => o.table === n && !o.paid);
  // en curso: sin pagar, o pagado al instante pero todavía no entregado ("otro" = mesa liberada a mano)
  const inProgress = (o) => !o.paid || (o.status !== "entregado" && o.payment_method !== "otro");
  const tableTotal = (n) => tableOrders(n).reduce((s, o) => s + o.items.reduce((a, i) => a + i.price * i.qty, 0), 0);
  const tableCashAlert = (n) => alerts.find((a) => a.type === "efectivo" && a.table === n);

  const nav = [
    { id: "pedidos", label: "pedidos", icon: <LayoutDashboard size={17} /> },
    { id: "mesas", label: "mapa de mesas", icon: <Armchair size={17} /> },
    { id: "cocina", label: "cocina y barra", icon: <ChefHat size={17} /> },
    { id: "caja", label: "caja del turno", icon: <Landmark size={17} /> },
    { id: "disponibilidad", label: "disponibilidad", icon: <Power size={17} /> },
    { id: "feedback", label: "feedback privado", icon: <Inbox size={17} /> },
  ];

  const cashTotal = Object.values(cash.by_method || {}).reduce((a, b) => a + b, 0);
  const tipsTotal = Object.values(cash.tips || {}).reduce((a, b) => a + b, 0);

  const approveOrder = async (orderId) => {
    const res = await post(`/orders/${orderId}/approve`, {});
    setState(res.state);
  };

  const waiterRanking = (state?.mozos || []).map((m) => {
    const wt = state?.waiter_tips?.[m.id] || { tips: 0, tables_served: 0, stars: 0 };
    const mentions = (state?.reviews || []).filter((r) => r.mozo_id === m.id).length;
    return { ...m, ...wt, mentions };
  }).sort((a, b) => b.tips - a.tips);

  const ItemLines = ({ items, highlight }) => (
    <div className="mt-2 space-y-1">
      {groupItems(items).map((it, i) => (
        <div key={i} className="text-xs">
          <span className="font-semibold">{it.qty}× {it.name}</span>
          {it.by && it.by !== "mozo" && <span className="text-[10px] font-mono2 text-neutral-500" data-testid="kanban-item-by"> · {it.by}</span>}
          {it.note && (
            highlight
              ? <p className="text-[11px] font-extrabold text-[#DC2626] bg-[#FEE2E2] rounded px-1.5 py-0.5 mt-0.5 inline-block">“{it.note}”</p>
              : <p className="text-[11px] text-neutral-500 italic">“{it.note}”</p>
          )}
        </div>
      ))}
    </div>
  );

  // ---------- secciones ----------

  const Pedidos = (
    <div className={`${isMobile ? "flex overflow-x-auto gap-3 pb-2 snap-x" : "grid grid-cols-4 gap-4"} h-full`} data-testid="kanban">
      {COLUMNS.map((col) => {
        const colOrders = orders.filter((o) => o.status === col.id && (col.id === "entregado" ? !o.paid : inProgress(o)));
        return (
          <div key={col.id}
            className={`bg-[#F2F2ED] rounded-lg p-3 flex flex-col min-h-0 transition ${isMobile ? "min-w-[82vw] snap-center" : ""} ${dragOver === col.id ? "kanban-col-over" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(col.id); }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(null);
              const id = e.dataTransfer.getData("orderId");
              const o = orders.find((x) => x.id === id);
              if (o && o.status !== col.id) moveOrder(id, col.id);
            }}
            data-testid={`kanban-col-${col.id}`}>
            <div className="flex items-center justify-between mb-3 px-1">
              <p className="font-extrabold text-sm lowercase">{col.label}</p>
              <span className="font-mono2 text-xs font-bold bg-white border border-[#E5E7EB] rounded-md px-1.5 py-0.5" data-testid={`kanban-count-${col.id}`}>{colOrders.length}</span>
            </div>
            <div className="space-y-3 overflow-y-auto flex-1 min-h-0 pr-0.5">
              <AnimatePresence initial={false}>
                {colOrders.map((o) => {
                  const tone = cardTone(o);
                  const mins = elapsedMin(o.created_at);
                  return (
                    <motion.div key={o.id} layoutId={mo.reduce ? undefined : o.id} layout={!mo.reduce}
                      initial={mo.reduce ? { opacity: 0 } : { y: -18, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      // la copia que sale de la columna vieja se va rápido y no recibe toques (evita un doble avance)
                      exit={{ opacity: 0, pointerEvents: "none", transition: { duration: 0.1 } }}
                      transition={mo.reduce ? { duration: 0.1 } : { layout: SPRING, y: SPRING, opacity: { duration: 0.2 } }}
                      draggable onDragStart={(e) => e.dataTransfer.setData("orderId", o.id)}
                      className={`relative overflow-hidden ${tone.bg} border-2 ${tone.border} rounded-lg p-3 cursor-grab active:cursor-grabbing transition-colors duration-200`}
                      data-testid={`order-card-${o.id}`}>
                      {/* destello celeste al entrar: capa aparte para no pisar el color de estado de la tarjeta */}
                      <motion.span aria-hidden="true" className="absolute inset-0 bg-[#A0CDE8] pointer-events-none"
                        initial={{ opacity: mo.reduce ? 0 : 0.7 }} animate={{ opacity: 0 }} transition={{ duration: 0.25, delay: mo.reduce ? 0 : 0.35 }} />
                      <div className="flex items-center justify-between">
                        <p className="font-extrabold text-sm">Mesa {o.table} <span className="font-mono2 text-xs font-semibold text-[#525252]" data-testid={`order-no-${o.id}`}>#{orderNo(o)}</span></p>
                        <span className={`flex items-center gap-1 font-mono2 text-[11px] font-bold ${mins >= 25 ? "text-[#DC2626]" : mins >= 15 ? "text-[#a16207]" : "text-neutral-500"}`}>
                          <Clock size={11} /> {mins} min
                        </span>
                      </div>
                      <ItemLines items={o.items} />
                      <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-[#0A0A0A]/10">
                        <span className="font-mono2 text-xs font-bold">{money(o.items.reduce((s, i) => s + i.price * i.qty, 0))}</span>
                        {o.status === "nuevo" && o.approved === false ? (
                          <button data-testid={`approve-${o.id}`} onClick={() => approveOrder(o.id)}
                            className="text-[11px] font-bold bg-[#16A34A] text-white px-2.5 py-1 rounded-md hover:brightness-110 active:scale-95 transition min-h-[36px]">
                            aprobar mesa
                          </button>
                        ) : NEXT[o.status] && (
                          <button data-testid={`advance-${o.id}`} onClick={() => moveOrder(o.id, NEXT[o.status])}
                            className="text-[11px] font-bold bg-white text-[#0A0A0A] border border-[#0A0A0A] px-2.5 py-1 rounded-md hover:bg-[#0A0A0A] hover:text-white active:scale-95 transition min-h-[44px]">
                            {NEXT[o.status] === "en_cocina" ? "a cocina →" : NEXT[o.status] === "listo" ? "listo →" : "entregado →"}
                          </button>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </div>
        );
      })}
    </div>
  );

  const Mesas = (
    <div className={`${isMobile ? "space-y-4" : "grid grid-cols-[1fr_320px] gap-6"} h-full`} data-testid="mesas-map">
      <div>
        <div className={`grid ${isMobile ? "grid-cols-3" : "grid-cols-4"} gap-3`}>
          {tables.map((tb) => {
            const st = TABLE_STATUS[tb.status] || TABLE_STATUS.libre;
            return (
              <button key={tb.number} data-testid={`table-${tb.number}`} onClick={() => setSelectedTable(tb.number)}
                className={`rounded-lg border-2 p-4 text-left transition hover:scale-[1.02] active:scale-[0.99] ${selectedTable === tb.number ? "ring-2 ring-[#0A0A0A]" : ""} ${tb.status === "efectivo_pendiente" ? "ring-cash" : ""}`}
                style={{ background: st.color, borderColor: st.border, color: st.text }}>
                <p className="font-extrabold text-base">{tb.name}</p>
                <p className="text-[11px] font-bold mt-0.5">{st.label}</p>
                {tb.status !== "libre" && <p className="font-mono2 text-xs font-bold mt-1.5">{money(tableTotal(tb.number))}</p>}
              </button>
            );
          })}
        </div>
        <div className="flex gap-4 mt-4 flex-wrap">
          {Object.entries(TABLE_STATUS).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5 text-xs font-semibold">
              <span className="w-3.5 h-3.5 rounded-md border" style={{ background: v.color, borderColor: v.border }} /> {v.label}
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 h-fit" data-testid="table-detail">
        {selectedTable ? (
          <>
            <div className="flex items-center justify-between">
              <p className="font-extrabold text-lg">Mesa {selectedTable}</p>
              <button data-testid="table-detail-close" onClick={() => setSelectedTable(null)}><X size={16} /></button>
            </div>
            <p className="text-xs text-neutral-500 mb-4">{(TABLE_STATUS[tables.find((x) => x.number === selectedTable)?.status] || {}).label}</p>
            {tableOrders(selectedTable).length ? (
              <>
                {tableOrders(selectedTable).map((o) => (
                  <div key={o.id} className="mb-3">
                    <p className="text-xs font-mono2 text-[#525252] uppercase">#{orderNo(o)} · {fmtTime(o.created_at)} · {o.status.replace("_", " ")}</p>
                    <ItemLines items={o.items} />
                  </div>
                ))}
                <div className="border-t border-dashed border-[#E5E7EB] pt-2 mt-2 flex justify-between font-mono2 font-bold">
                  <span>cuenta</span><span data-testid="table-detail-total">{money(tableTotal(selectedTable))}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {tableCashAlert(selectedTable) ? (
                    <button data-testid="table-cobrar" onClick={() => confirmCash(selectedTable, tableCashAlert(selectedTable).tip || 0)}
                      className="col-span-2 bg-[#DC2626] text-white text-xs font-bold py-2.5 rounded-lg hover:brightness-110 transition">
                      confirmar cobro en efectivo · {money((tableCashAlert(selectedTable).amount || 0) + (tableCashAlert(selectedTable).tip || 0))}
                    </button>
                  ) : (
                    <p className="col-span-2 text-[11px] text-neutral-400">El cobro se hace desde la alerta o desde el cliente.</p>
                  )}
                  <button data-testid="table-liberar" onClick={() => freeTable(selectedTable)}
                    className="col-span-2 border-2 border-[#0A0A0A] text-xs font-bold py-2.5 rounded-lg hover:bg-[#0A0A0A] hover:text-white transition">
                    liberar mesa (cierra la cuenta)
                  </button>
                </div>
              </>
            ) : (
              <p className="text-sm text-neutral-500">Mesa sin consumo activo.</p>
            )}
          </>
        ) : (
          <p className="text-sm text-neutral-500">Elegí una mesa para ver el detalle de su cuenta.</p>
        )}
      </div>
    </div>
  );

  const Cocina = (
    <div className="space-y-6" data-testid="cocina-tickets">
      <div>
        <p className="font-extrabold lowercase mb-3 flex items-center gap-2"><ChefHat size={17} /> cocina</p>
        <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-3"} gap-4`}>
          {orders.filter((o) => o.status === "en_cocina" && inProgress(o)).map((o) => {
            const kitchenItems = o.items.filter((it) => KITCHEN_CATS.includes(it.category));
            if (!kitchenItems.length) return null;
            return (
              <div key={o.id} className="bg-white border-2 border-[#0A0A0A] rounded-lg p-5" data-testid={`ticket-${o.id}`}>
                <div className="flex items-center justify-between border-b-2 border-dashed border-[#0A0A0A]/20 pb-3 mb-3">
                  <p className="font-extrabold text-2xl">Mesa {o.table}</p>
                  <span className="font-mono2 text-xs font-bold bg-[#FEF9C3] text-[#a16207] px-2 py-1 rounded-md">{elapsedMin(o.created_at)} min</span>
                </div>
                <ItemLines items={kitchenItems} highlight />
                <button data-testid={`kitchen-done-${o.id}`} onClick={() => moveOrder(o.id, "listo")}
                  className="mt-4 w-full bg-[#16A34A] text-white font-bold py-3 rounded-lg hover:brightness-95 active:scale-[0.98] transition">
                  listo ✓
                </button>
              </div>
            );
          })}
          {orders.filter((o) => o.status === "en_cocina" && inProgress(o) && o.items.some((it) => KITCHEN_CATS.includes(it.category))).length === 0 && (
            <p className="col-span-3 text-center text-neutral-500 py-10">No hay nada en preparación ahora.</p>
          )}
        </div>
      </div>
      <div>
        <p className="font-extrabold lowercase mb-3 flex items-center gap-2"><Wine size={17} /> barra <span className="text-xs font-normal text-neutral-400">(bebidas y vinos no pasan por cocina)</span></p>
        <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-3"} gap-4`}>
          {orders.filter((o) => ["en_cocina", "listo"].includes(o.status) && inProgress(o)).map((o) => {
            const barItems = o.items.filter((it) => !KITCHEN_CATS.includes(it.category));
            if (!barItems.length) return null;
            return (
              <div key={o.id} className="bg-[#E3F0F9] border-2 border-[#A0CDE8] rounded-lg p-5" data-testid={`bar-ticket-${o.id}`}>
                <div className="flex items-center justify-between border-b-2 border-dashed border-[#0A0A0A]/10 pb-2.5 mb-2.5">
                  <p className="font-extrabold text-lg">Mesa {o.table}</p>
                  <span className="font-mono2 text-xs font-bold text-neutral-500">{elapsedMin(o.created_at)} min</span>
                </div>
                <ItemLines items={barItems} highlight />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const Caja = (
    <div className={`${isMobile ? "space-y-4" : "grid grid-cols-2 gap-6"}`} data-testid="caja-panel">
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-extrabold lowercase">cobros del turno</p>
          <span className={`text-[11px] font-bold px-2 py-1 rounded-md ${cash.closed ? "bg-[#FEE2E2] text-[#DC2626]" : "bg-[#DCFCE7] text-[#16A34A]"}`}>
            {cash.closed ? "cerrada" : "abierta"}
          </span>
        </div>
        <div className="space-y-2.5">
          {Object.entries(cash.by_method || {}).map(([m, v]) => (
            <div key={m} className="flex justify-between items-center text-sm">
              <span className="font-semibold">{METHOD_LABELS[m]}</span>
              <span className="font-mono2 font-bold" data-testid={`cash-${m}`}>{money(v)}</span>
            </div>
          ))}
          <div className="border-t border-[#E5E7EB] pt-2.5 flex justify-between font-mono2 font-bold">
            <span>total cobrado</span><span data-testid="cash-total">{money(cashTotal)}</span>
          </div>
          <div className="flex justify-between text-sm text-neutral-500">
            <span>propinas</span><span className="font-mono2">{money(tipsTotal)}</span>
          </div>
        </div>
        {!cash.closed ? (
          <button data-testid="close-cash-btn" onClick={closeCash}
            className="mt-5 w-full bg-[#0A0A0A] text-white font-bold py-3 rounded-lg hover:bg-neutral-800 transition">
            cerrar caja
          </button>
        ) : (
          <button data-testid="open-cash-btn" onClick={openCash}
            className="mt-5 w-full bg-[#A0CDE8] text-[#0A0A0A] font-bold py-3 rounded-lg hover:brightness-95 transition">
            abrir nuevo turno
          </button>
        )}
      </div>
      <div className="space-y-6">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
          <p className="font-extrabold lowercase mb-4">propinas por mozo del turno</p>
          <div className="space-y-2.5" data-testid="waiter-ranking">
            {waiterRanking.map((w, i) => (
              <div key={w.id} className="flex items-center gap-3" data-testid={`waiter-${w.id}`}>
                <span className="w-7 h-7 rounded-lg bg-[#A0CDE8] flex items-center justify-center font-extrabold text-xs">{w.name[0]}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{w.name}</p>
                  <p className="text-[10px] text-neutral-500">{w.tables_served} mesas · {w.stars}★ · {w.mentions} reseñas</p>
                </div>
                <span className="font-mono2 text-sm font-bold" data-testid={`waiter-tips-${w.id}`}>{money(w.tips)}</span>
                {i === 0 && <span className="text-[10px] font-bold bg-[#DCFCE7] text-[#16A34A] px-1.5 py-0.5 rounded-md">top</span>}
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
          <p className="font-extrabold lowercase mb-4">efectivo</p>
          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between"><span>esperado</span><span className="font-mono2 font-bold" data-testid="cash-esperado">{money(cash.esperado_efectivo || 0)}</span></div>
            <div className="flex justify-between"><span>confirmado</span><span className="font-mono2 font-bold text-[#16A34A]" data-testid="cash-confirmado">{money(cash.confirmado_efectivo || 0)}</span></div>
            <div className="flex justify-between border-t border-[#E5E7EB] pt-2.5">
              <span className="font-bold">diferencia</span>
              <span className={`font-mono2 font-bold ${(cash.esperado_efectivo || 0) - (cash.confirmado_efectivo || 0) > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>
                {money((cash.esperado_efectivo || 0) - (cash.confirmado_efectivo || 0))}
              </span>
            </div>
          </div>
        </div>
        {cash.closed && (
          <div className="bg-[#0A0A0A] text-white rounded-lg p-6" data-testid="cash-summary">
            <p className="font-extrabold lowercase mb-3">resumen de cierre</p>
            <div className="space-y-1.5 text-sm font-mono2">
              <div className="flex justify-between"><span>cobrado</span><span>{money(cashTotal)}</span></div>
              <div className="flex justify-between"><span>propinas</span><span>{money(tipsTotal)}</span></div>
              <div className="flex justify-between font-bold text-[#A0CDE8] pt-2 border-t border-white/20">
                <span>total del turno</span><span>{money(cashTotal + tipsTotal)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const Disponibilidad = (
    <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-2"} gap-3`} data-testid="disponibilidad-grid">
      {dishes.map((d) => (
        <div key={d.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex items-center gap-3">
          <img src={d.photo} alt={d.name} className={`w-14 h-14 rounded-lg object-cover ${!d.available ? "grayscale opacity-50" : ""}`} loading="lazy" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm truncate">{d.name}</p>
            <p className="text-xs text-neutral-500 font-mono2">{money(d.price)}</p>
          </div>
          <button data-testid={`toggle-dish-${d.id}`} onClick={() => toggleDish(d)}
            className={`switch-toggle relative w-12 h-7 rounded-full transition-colors ${d.available ? "bg-[#16A34A]" : "bg-neutral-300"}`}>
            <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${d.available ? "left-6" : "left-1"}`} />
          </button>
        </div>
      ))}
    </div>
  );

  const Feedback = (
    <div className="space-y-3" data-testid="feedback-list">
      {reviews.filter((r) => !r.handled).map((r) => (
        <div key={r.id} className="bg-white border border-[#E5E7EB] rounded-lg p-5 flex items-start gap-4" data-testid={`feedback-${r.id}`}>
          <div className="shrink-0 w-12 h-12 rounded-lg bg-[#FEE2E2] flex items-center justify-center">
            <span className="font-mono2 font-extrabold text-[#DC2626]">{r.stars}★</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 text-xs text-neutral-500 flex-wrap">
              <span className="font-bold text-[#0A0A0A]">Mesa {r.table}</span>
              <span className="font-mono2" data-testid={`feedback-date-${r.id}`}>{fmtDateTime(r.at)}</span>
            </div>
            <p className="text-sm mt-1">{r.text}</p>
            {r.whatsapp && <p className="text-xs text-neutral-500 mt-1 font-mono2" data-testid={`feedback-wa-${r.id}`}>{r.whatsapp}</p>}
          </div>
          <div className="shrink-0 flex flex-col gap-2">
            {r.whatsapp && (
              <button data-testid={`contact-${r.id}`} onClick={() => window.open(`https://wa.me/${r.whatsapp.replace(/\D/g, "")}`, "_blank")}
                className="flex items-center gap-1.5 text-xs font-bold bg-[#16A34A] text-white px-3 py-2 rounded-lg hover:brightness-110 transition">
                <MessageCircle size={13} /> contactar
              </button>
            )}
            <button data-testid={`handle-${r.id}`} onClick={() => handleReview(r.id)}
              className="text-xs font-bold bg-[#0A0A0A] text-white px-3 py-2 rounded-lg hover:bg-neutral-800 transition">
              marcar gestionada
            </button>
          </div>
        </div>
      ))}
      {reviews.filter((r) => !r.handled).length === 0 && (
        <p className="text-center text-neutral-500 py-16">No hay feedback privado pendiente. Buen trabajo.</p>
      )}
    </div>
  );

  const sections = { pedidos: Pedidos, mesas: Mesas, cocina: Cocina, caja: Caja, disponibilidad: Disponibilidad, feedback: Feedback };

  const NavContent = (
    <>
      <nav className="flex-1 p-3 space-y-1">
        {nav.map((n) => (
          <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => { setSection(n.id); setDrawerOpen(false); }}
            className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-semibold transition ${section === n.id ? "bg-[#A0CDE8] text-[#0A0A0A]" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
            {n.icon} {n.label}
            {n.id === "pedidos" && orders.filter((o) => o.status === "nuevo" && inProgress(o)).length > 0 && (
              <motion.span key={orders.filter((o) => o.status === "nuevo" && inProgress(o)).length}
                initial={{ scale: mo.reduce ? 1 : 1.6 }} animate={{ scale: 1 }} transition={mo.reduce ? { duration: 0.1 } : SPRING}
                className="ml-auto bg-[#DC2626] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="nuevos-badge">
                {orders.filter((o) => o.status === "nuevo" && inProgress(o)).length}
              </motion.span>
            )}
            {n.id === "feedback" && reviews.filter((r) => !r.handled).length > 0 && (
              <span className="ml-auto bg-[#DC2626] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {reviews.filter((r) => !r.handled).length}
              </span>
            )}
          </button>
        ))}
      </nav>
      <div className="p-4 border-t border-white/10">
        <button data-testid="sound-toggle" onClick={() => setMuted(!muted)}
          className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-semibold text-white/70 hover:bg-white/10 hover:text-white transition">
          {muted ? <VolumeX size={17} /> : <Volume2 size={17} />} {muted ? "sonido apagado" : "sonido activado"}
        </button>
      </div>
    </>
  );

  return (
    <div className="h-full flex" data-testid="gerencia-view">
      {!isMobile && (
        <aside className="w-60 shrink-0 bg-[#0A0A0A] text-white flex flex-col">
          <div className="p-5 flex items-center gap-2.5 border-b border-white/10">
            <Logo size={34} />
            <div>
              <p className="font-extrabold lowercase leading-none">bodegón demo</p>
              <p className="text-[10px] text-white/50 uppercase tracking-widest mt-0.5">gerencia</p>
            </div>
          </div>
          {NavContent}
        </aside>
      )}

      {/* drawer mobile */}
      {isMobile && drawerOpen && (
        <div className="fixed inset-0 z-50 flex" data-testid="gerencia-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <aside className="relative w-64 bg-[#0A0A0A] text-white flex flex-col h-full">
            <div className="p-5 flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-2.5">
                <Logo size={30} />
                <p className="font-extrabold lowercase">gerencia</p>
              </div>
              <button data-testid="drawer-close" onClick={() => setDrawerOpen(false)}><X size={18} /></button>
            </div>
            {NavContent}
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <div className="shrink-0 bg-white border-b border-[#E5E7EB] px-4 py-2.5 space-y-2" data-testid="alerts-bar">
          <div className="flex items-center gap-2">
            {isMobile && (
              <button data-testid="gerencia-menu-btn" onClick={() => setDrawerOpen(true)}
                className="shrink-0 w-8 h-8 rounded-lg border border-[#E5E7EB] flex items-center justify-center">
                <Menu size={16} />
              </button>
            )}
            {alerts.length === 0 ? (
              <p className="text-xs text-neutral-400 font-semibold flex items-center gap-2"><BellRing size={13} /> sin alertas activas</p>
            ) : (
              <p className="text-xs text-neutral-400 font-semibold">{alerts.length} alerta{alerts.length > 1 ? "s" : ""} activa{alerts.length > 1 ? "s" : ""}</p>
            )}
          </div>
          <AnimatePresence initial={false}>
            {alerts.slice(0, 3).map((a) => (
              <motion.div key={a.id} data-testid={`alert-${a.id}`} layout={!mo.reduce}
                initial={mo.reduce ? { opacity: 0 } : { y: -16, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={mo.reduce ? { opacity: 0 } : { scale: 0.9, opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }}
                transition={mo.reduce ? { duration: 0.1 } : SPRING}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-bold ${a.type === "efectivo" ? "bg-[#FEE2E2] text-[#DC2626] border-2 border-[#DC2626] ring-cash" : "bg-[#FEF9C3] text-[#a16207] border border-[#EAB308]"}`}>
                {a.type === "efectivo" ? <Banknote size={16} className="shrink-0" /> : <Hand size={15} className="shrink-0" />}
                <span className="flex-1 text-xs sm:text-sm">{a.message}</span>
                {a.type === "efectivo" ? (
                  <button data-testid={`confirm-cash-${a.table}`} onClick={() => confirmCash(a.table, a.tip || 0)}
                    className="shrink-0 bg-[#DC2626] text-white text-xs font-bold px-3 py-1.5 rounded-md hover:brightness-110 active:scale-95 transition">
                    confirmar cobro
                  </button>
                ) : (
                  <button data-testid={`resolve-${a.id}`} onClick={() => resolveAlert(a.id)}
                    className="shrink-0 bg-[#0A0A0A] text-white text-xs font-bold px-3 py-1.5 rounded-md active:scale-95 transition">
                    <Check size={13} />
                  </button>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <h1 className="text-2xl font-extrabold lowercase" data-testid="gerencia-section-title">{nav.find((n) => n.id === section)?.label}</h1>
            {!isMobile && <span className="font-mono2 text-xs text-neutral-400">{new Date().toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" })}</span>}
          </div>
          {sections[section]}
        </div>
      </div>
    </div>
  );
}
